import React, { MouseEvent, useState } from 'react'

import { isTouchDevice } from '../../Util'

import { ButtonContainer, ButtonText } from './style'

type Props = {
  backgroundcolor: string
  fontcolor: string
  activefontcolor?: string
  hoversensitive?: boolean
  onPress: (event: MouseEvent<HTMLButtonElement>) => void
  isInline?: boolean
  customPadding?: string
  customMargin?: string
  disableHoverOnTouchDevice?: boolean
  isMaxWidth?: boolean
  tagManagerId?: string
  isRender?: boolean
  /**
   * Forwarded by styled-components when this component is wrapped via
   * `styled(ButtonWithText)` (e.g. DefaultButtonWithText). Required for
   * extension styles to apply to the rendered <button>.
   */
  className?: string
}

/**
 * OB-499: Native <button> replaces styled(TouchableOpacity). Public prop
 * `onPress` is preserved so all 22 consumer files compile unchanged. The
 * underlying button has type="button" so it never submits an enclosing form.
 */
export const ButtonWithText: React.FC<Props> = (props) => {
  const {
    backgroundcolor,
    fontcolor,
    activefontcolor,
    hoversensitive,
    children,
    isInline = false,
    customPadding,
    customMargin,
    disableHoverOnTouchDevice = false,
    isMaxWidth,
    tagManagerId,
    isRender = true,
    className,
  } = props
  const [buttonActiveState, setButtonActiveState] = useState<boolean>(false)

  const mouseEnterEvent = () => setButtonActiveState(!!hoversensitive)
  const mouseLeaveEvent = () => setButtonActiveState(false)

  const disableHover: boolean = disableHoverOnTouchDevice
    ? isTouchDevice()
    : false

  if (!isRender) {
    return null
  }

  return (
    <ButtonContainer
      type="button"
      className={className}
      backgroundcolor={backgroundcolor}
      fontcolor={fontcolor}
      $buttonstate={buttonActiveState}
      onMouseEnter={disableHover ? undefined : mouseEnterEvent}
      onMouseLeave={disableHover ? undefined : mouseLeaveEvent}
      custompadding={customPadding}
      custommargin={customMargin}
      onClick={props.onPress}
      isInline={isInline}
      isMaxWidth={isMaxWidth}
      id={tagManagerId || undefined}
      data-component="button-with-text"
    >
      <ButtonText
        fontcolor={fontcolor}
        activefontcolor={activefontcolor || backgroundcolor}
        $buttonstate={buttonActiveState}
      >
        {children}
      </ButtonText>
    </ButtonContainer>
  )
}
